import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  MessageCircle, Mail, Send, Settings, RefreshCw, 
  CheckCircle, XCircle, Clock, AlertTriangle, Activity,
  Smartphone, Inbox, BarChart3, Eye, Play
} from 'lucide-react';
import api from '@/lib/api';

export default function DnaPayMensageria() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(null);
  
  // Configuração editável
  const [editConfig, setEditConfig] = useState({
    whatsapp_enabled: false,
    whatsapp_api_url: '',
    whatsapp_api_token: '',
    whatsapp_phone_id: '',
    whatsapp_business_account_id: '',
    email_enabled: false,
    email_provider: 'sendgrid',
    email_api_key: '',
    email_from_address: 'financeiro@novodna.com.br',
    email_from_name: 'Novo DNA - Financeiro'
  });
  
  // Teste
  const [testeWhatsapp, setTesteWhatsapp] = useState({ telefone: '', mensagem: 'Mensagem de teste do DNA Pay' });
  const [testeEmail, setTesteEmail] = useState({ email: '', subject: 'Teste DNA Pay', body: '<p>Esta é uma mensagem de teste.</p>' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes] = await Promise.all([
        api.get('/dnapay/messaging/config'),
        api.get('/dnapay/messaging/estatisticas')
      ]);
      
      setConfig(configRes.data);
      setEstatisticas(statsRes.data);
      
      // Atualizar form com config atual (exceto tokens mascarados)
      if (configRes.data?.config) {
        setEditConfig(prev => ({
          ...prev,
          whatsapp_enabled: configRes.data.config.whatsapp_enabled,
          whatsapp_api_url: configRes.data.config.whatsapp_api_url,
          whatsapp_phone_id: configRes.data.config.whatsapp_phone_id,
          whatsapp_business_account_id: configRes.data.config.whatsapp_business_account_id,
          email_enabled: configRes.data.config.email_enabled,
          email_provider: configRes.data.config.email_provider,
          email_from_address: configRes.data.config.email_from_address,
          email_from_name: configRes.data.config.email_from_name
        }));
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const salvarConfig = async () => {
    setSalvando(true);
    try {
      await api.post('/dnapay/messaging/config', null, {
        params: editConfig
      });
      toast.success('Configurações salvas com sucesso!');
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSalvando(false);
    }
  };

  const testarWhatsapp = async () => {
    if (!testeWhatsapp.telefone) {
      toast.error('Informe o telefone');
      return;
    }
    
    setTestando('whatsapp');
    try {
      const res = await api.post('/dnapay/messaging/testar-whatsapp', null, {
        params: {
          telefone: testeWhatsapp.telefone,
          mensagem: testeWhatsapp.mensagem
        }
      });
      
      if (res.data?.success) {
        toast.success('WhatsApp enviado com sucesso!');
      } else {
        toast.error(res.data?.error || 'Falha no envio');
      }
    } catch (error) {
      toast.error('Erro ao testar WhatsApp');
    } finally {
      setTestando(null);
    }
  };

  const testarEmail = async () => {
    if (!testeEmail.email) {
      toast.error('Informe o email');
      return;
    }
    
    setTestando('email');
    try {
      const res = await api.post('/dnapay/messaging/testar-email', null, {
        params: {
          email_to: testeEmail.email,
          subject: testeEmail.subject,
          body: testeEmail.body
        }
      });
      
      if (res.data?.success) {
        toast.success('Email enviado com sucesso!');
      } else {
        toast.error(res.data?.error || 'Falha no envio');
      }
    } catch (error) {
      toast.error('Erro ao testar Email');
    } finally {
      setTestando(null);
    }
  };

  const processarFila = async () => {
    try {
      const res = await api.post('/dnapay/messaging/processar-fila');
      toast.success(`Fila processada: ${res.data?.processados || 0} mensagens`);
      loadData();
    } catch (error) {
      toast.error('Erro ao processar fila');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dnapay-mensageria">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Central de Mensageria
          </h1>
          <p className="text-muted-foreground">Configuração de WhatsApp e Email</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={processarFila}>
            <Play className="w-4 h-4 mr-2" />
            Processar Fila
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">WhatsApp</p>
                <p className="text-lg font-bold">
                  {config?.status?.whatsapp === 'ativo' ? 'Ativo' : 'Inativo'}
                </p>
              </div>
              <Smartphone className={`w-8 h-8 ${config?.status?.whatsapp === 'ativo' ? 'text-green-500' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="text-lg font-bold">
                  {config?.status?.email === 'ativo' ? 'Ativo' : 'Inativo'}
                </p>
              </div>
              <Mail className={`w-8 h-8 ${config?.status?.email === 'ativo' ? 'text-blue-500' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Enviadas (24h)</p>
                <p className="text-2xl font-bold">{estatisticas?.enviados_24h || 0}</p>
              </div>
              <Send className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Na Fila</p>
                <p className="text-2xl font-bold">{estatisticas?.por_status?.agendada || 0}</p>
              </div>
              <Inbox className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="config" data-testid="tab-config">
            <Settings className="w-4 h-4 mr-2" />
            Configurações
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            <Smartphone className="w-4 h-4 mr-2" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="estatisticas" data-testid="tab-stats">
            <BarChart3 className="w-4 h-4 mr-2" />
            Estatísticas
          </TabsTrigger>
        </TabsList>

        {/* Tab Configurações */}
        <TabsContent value="config" className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              As integrações de WhatsApp e Email estão em modo <strong>MOCKADO</strong>. 
              Configure suas chaves de API para ativar o envio real.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* WhatsApp Config */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-green-600" />
                  WhatsApp Business API
                </CardTitle>
                <CardDescription>Configure a integração com WhatsApp</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Habilitar WhatsApp</Label>
                  <Switch
                    checked={editConfig.whatsapp_enabled}
                    onCheckedChange={(v) => setEditConfig({ ...editConfig, whatsapp_enabled: v })}
                  />
                </div>
                
                <div>
                  <Label>API URL</Label>
                  <Input
                    placeholder="https://graph.facebook.com/v18.0"
                    value={editConfig.whatsapp_api_url}
                    onChange={(e) => setEditConfig({ ...editConfig, whatsapp_api_url: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>API Token</Label>
                  <Input
                    type="password"
                    placeholder="Token de acesso"
                    value={editConfig.whatsapp_api_token}
                    onChange={(e) => setEditConfig({ ...editConfig, whatsapp_api_token: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Phone Number ID</Label>
                  <Input
                    placeholder="ID do número de telefone"
                    value={editConfig.whatsapp_phone_id}
                    onChange={(e) => setEditConfig({ ...editConfig, whatsapp_phone_id: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Business Account ID</Label>
                  <Input
                    placeholder="ID da conta business"
                    value={editConfig.whatsapp_business_account_id}
                    onChange={(e) => setEditConfig({ ...editConfig, whatsapp_business_account_id: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Email Config */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Serviço de Email
                </CardTitle>
                <CardDescription>Configure SendGrid ou Resend</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Habilitar Email</Label>
                  <Switch
                    checked={editConfig.email_enabled}
                    onCheckedChange={(v) => setEditConfig({ ...editConfig, email_enabled: v })}
                  />
                </div>
                
                <div>
                  <Label>Provedor</Label>
                  <Select
                    value={editConfig.email_provider}
                    onValueChange={(v) => setEditConfig({ ...editConfig, email_provider: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="resend">Resend</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    placeholder="Chave de API"
                    value={editConfig.email_api_key}
                    onChange={(e) => setEditConfig({ ...editConfig, email_api_key: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Email Remetente</Label>
                  <Input
                    placeholder="email@dominio.com"
                    value={editConfig.email_from_address}
                    onChange={(e) => setEditConfig({ ...editConfig, email_from_address: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Nome Remetente</Label>
                  <Input
                    placeholder="Nome para exibição"
                    value={editConfig.email_from_name}
                    onChange={(e) => setEditConfig({ ...editConfig, email_from_name: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <Button onClick={salvarConfig} disabled={salvando} className="w-full">
            {salvando ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Salvar Configurações
          </Button>
        </TabsContent>

        {/* Tab WhatsApp Teste */}
        <TabsContent value="whatsapp">
          <Card>
            <CardHeader>
              <CardTitle>Testar Envio WhatsApp</CardTitle>
              <CardDescription>Envie uma mensagem de teste</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editConfig.whatsapp_enabled && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    WhatsApp está desabilitado. Ative nas configurações.
                  </AlertDescription>
                </Alert>
              )}
              
              <div>
                <Label>Telefone (com DDD)</Label>
                <Input
                  placeholder="11999999999"
                  value={testeWhatsapp.telefone}
                  onChange={(e) => setTesteWhatsapp({ ...testeWhatsapp, telefone: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Mensagem</Label>
                <Input
                  placeholder="Mensagem de teste"
                  value={testeWhatsapp.mensagem}
                  onChange={(e) => setTesteWhatsapp({ ...testeWhatsapp, mensagem: e.target.value })}
                />
              </div>
              
              <Button 
                onClick={testarWhatsapp} 
                disabled={testando === 'whatsapp' || !editConfig.whatsapp_enabled}
                className="bg-green-600 hover:bg-green-700"
              >
                {testando === 'whatsapp' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Enviar Teste
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Email Teste */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>Testar Envio de Email</CardTitle>
              <CardDescription>Envie um email de teste</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editConfig.email_enabled && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Email está desabilitado. Ative nas configurações.
                  </AlertDescription>
                </Alert>
              )}
              
              <div>
                <Label>Email Destinatário</Label>
                <Input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={testeEmail.email}
                  onChange={(e) => setTesteEmail({ ...testeEmail, email: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Assunto</Label>
                <Input
                  placeholder="Assunto do email"
                  value={testeEmail.subject}
                  onChange={(e) => setTesteEmail({ ...testeEmail, subject: e.target.value })}
                />
              </div>
              
              <Button 
                onClick={testarEmail} 
                disabled={testando === 'email' || !editConfig.email_enabled}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {testando === 'email' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Enviar Teste
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Estatísticas */}
        <TabsContent value="estatisticas">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Por Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(estatisticas?.por_status || {}).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="capitalize">{status}</span>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(estatisticas?.por_status || {}).length === 0 && (
                    <p className="text-muted-foreground text-center py-4">Nenhum dado disponível</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por Canal</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(estatisticas?.por_canal || {}).map(([canal, count]) => (
                    <div key={canal} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        {canal === 'whatsapp' ? (
                          <Smartphone className="w-4 h-4 text-green-600" />
                        ) : (
                          <Mail className="w-4 h-4 text-blue-600" />
                        )}
                        <span className="capitalize">{canal}</span>
                      </div>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(estatisticas?.por_canal || {}).length === 0 && (
                    <p className="text-muted-foreground text-center py-4">Nenhum dado disponível</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
